import type { ProjectionCoverage } from "../domain/coverage";
import { buildCanonicalProposal } from "../domain/proposal";
import type { AnonymousFixture } from "../domain/types";
import { sha256Hex, type LocalIfcObservation } from "./local-ifc";
import { localIfcDisplayReceipt } from "./local-ifc-receipt";

const proposalProbeLimit = 1_024;
const proposalRequest = { property: "WorkbenchParityMarker", value: "t0-parity-v1" } as const;

export interface T0ParitySnapshot {
  readonly schemaVersion: "t0-parity-snapshot/1";
  readonly sourceSha256: string;
  readonly receiptSha256: string;
  readonly recordClosureSha256: string;
  readonly proposalSha256: string;
  readonly records: number;
  readonly proposalProbeScope: "all-records" | "first-1024-by-id";
  readonly proposalAssessedElementCount: number;
}

async function jsonSha256(value: unknown): Promise<string> {
  return sha256Hex(new TextEncoder().encode(JSON.stringify(value)));
}

export async function buildT0ParitySnapshot(
  observation: LocalIfcObservation,
  fixture: AnonymousFixture,
  coverage: ProjectionCoverage
): Promise<T0ParitySnapshot> {
  const sortedIds = fixture.elements.map((element) => element.id).sort();
  const proposalIds = sortedIds.slice(0, proposalProbeLimit);
  const proposal = buildCanonicalProposal(fixture, new Set(proposalIds), proposalRequest);
  const closure = {
    records: coverage.records,
    eligibleGeometryRecords: coverage.eligibleGeometryRecords,
    renderable: coverage.renderable,
    missingGeometryRecords: coverage.missingGeometryRecords,
    unsupportedGeometryRecords: coverage.unsupportedGeometryRecords,
    nonGeometricRetainedRecords: coverage.nonGeometricRetainedRecords,
    unclassifiedRetainedRecords: coverage.unclassifiedRetainedRecords,
    producerSkippedRecords: coverage.producerSkippedRecords
  };
  return {
    schemaVersion: "t0-parity-snapshot/1",
    sourceSha256: observation.source.sha256,
    receiptSha256: await jsonSha256(localIfcDisplayReceipt(observation)),
    recordClosureSha256: await jsonSha256(closure),
    proposalSha256: await jsonSha256(proposal),
    records: coverage.records,
    proposalProbeScope: sortedIds.length <= proposalProbeLimit ? "all-records" : "first-1024-by-id",
    proposalAssessedElementCount: proposal.validation.assessedElementCount
  };
}
